import { useState } from "react";
import { ExpressionEditor } from "../common/LazyExpressionEditor";
import { XIcon } from "../shared/Icons";

const labelStyle: React.CSSProperties = { fontSize: 12, color: "var(--ink-secondary)", marginBottom: 6 };
const inputStyle: React.CSSProperties = {
  padding: "7px 10px", background: "var(--surface-input)", border: "1px solid var(--line)",
  borderRadius: 8, color: "var(--ink)", fontSize: 13, outline: "none", width: "100%",
  boxSizing: "border-box",
};
// ponytail: field-sizing = auto-grow nativo até 5 linhas; navegador sem suporte fica em 1 linha + arrasto manual. Se pesar, onInput com scrollHeight.
export const growStyle = { fieldSizing: "content", minHeight: "calc(1.5em + 18px)", maxHeight: "calc(7.5em + 18px)", lineHeight: 1.5, resize: "vertical", overflowY: "auto" } as React.CSSProperties;
const tabBtn = (active: boolean): React.CSSProperties => ({
  background: active ? "var(--line)" : "none", color: active ? "var(--ink)" : "var(--ink-secondary)",
  border: "1px solid var(--line-strong)", borderRadius: 6, padding: "2px 10px", fontSize: 11, cursor: "pointer",
});

type Mode = "form" | "json";
type Row = { k: string; v: string; ai: boolean };

function toRows(json: string): Row[] | null {
  // objeto plano string→escalar vira linhas; senão null (não dá pra editar em form)
  try {
    const o = JSON.parse(json || "{}");
    if (!o || typeof o !== "object" || Array.isArray(o)) return null;
    const rows: Row[] = [];
    for (const [k, val] of Object.entries(o)) {
      if (val !== null && typeof val === "object") return null;
      const s = String(val ?? "");
      const m = s.match(/^\[\[\s*(\w+)\s*\]\]$/);
      rows.push(m ? { k, v: "", ai: true } : { k, v: s, ai: false });
    }
    return rows;
  } catch {
    return null;
  }
}

function rowsToJson(rows: Row[]): string {
  const o: Record<string, string> = {};
  for (const r of rows) if (r.k.trim()) o[r.k] = r.ai ? `[[${r.k}]]` : r.v;
  return JSON.stringify(o, null, 2);
}

export function SectionEditor({
  label, value, onChange, defaultMode = "form", aiFields, onAiFieldsChange, aiNameCounts,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  defaultMode?: Mode;
  aiFields?: Record<string, string>;
  onAiFieldsChange?: (next: Record<string, string>) => void;
  aiNameCounts?: Record<string, number>;
}) {
  const [mode, setMode] = useState<Mode>(() =>
    defaultMode === "form" && toRows(value) === null ? "json" : defaultMode,
  );
  const [rows, setRows] = useState<Row[]>(() => toRows(value) ?? []);
  const [notice, setNotice] = useState("");

  function commitRows(next: Row[], removedAiRow: Row | null) {
    setRows(next);
    onChange(rowsToJson(next));
    if (removedAiRow && removedAiRow.ai && removedAiRow.k && onAiFieldsChange) {
      const stillUsed = next.some((r) => r.ai && r.k === removedAiRow.k);
      if (!stillUsed) {
        const reg = { ...(aiFields ?? {}) };
        delete reg[removedAiRow.k];
        onAiFieldsChange(reg);
      }
    }
  }

  function switchMode(next: Mode) {
    if (next === mode) return;
    if (next === "form") {
      const parsed = toRows(value);
      if (parsed === null) {
        setNotice("conteúdo aninhado — edite no modo JSON");
        return;
      }
      setNotice("");
      setRows(parsed);
    }
    setMode(next);
  }

  const keyCounts: Record<string, number> = {};
  for (const r of rows) if (r.k.trim()) keyCounts[r.k] = (keyCounts[r.k] || 0) + 1;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={labelStyle}>{label}</span>
        <div style={{ display: "flex", gap: 4 }}>
          <button type="button" style={tabBtn(mode === "form")} onClick={() => switchMode("form")}>Formulário</button>
          <button type="button" style={tabBtn(mode === "json")} onClick={() => switchMode("json")}>JSON</button>
        </div>
      </div>
      {notice && <span style={{ fontSize: 11, color: "var(--warn)" }}>{notice}</span>}
      {mode === "json" ? (
        <ExpressionEditor
          value={value}
          onChange={onChange}
          minHeight={80}
          aiFields={aiFields}
          onAiFieldsChange={onAiFieldsChange}
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {rows.map((r, i) => (
            <div key={i} style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                style={{ ...inputStyle, flex: 1, ...((r.ai ? (aiNameCounts?.[r.k] ?? 0) > 1 : (keyCounts[r.k] ?? 0) > 1) ? { borderColor: "var(--brand)" } : {}) }}
                placeholder="chave"
                value={r.k}
                title={
                  r.ai && (aiNameCounts?.[r.k] ?? 0) > 1
                    ? "campo da IA usado em mais de um lugar — a descrição é compartilhada (renomeie para separar)"
                    : keyCounts[r.k] > 1
                      ? "chave repetida nesta seção"
                      : undefined
                }
                onChange={(e) => {
                  const newK = e.target.value;
                  if (r.ai && onAiFieldsChange && newK !== r.k) {
                    const reg = { ...(aiFields ?? {}) };
                    const d = reg[r.k];
                    delete reg[r.k];
                    if (newK.trim()) reg[newK] = d ?? "";
                    onAiFieldsChange(reg);
                  }
                  commitRows(rows.map((x, j) => (j === i ? { ...x, k: newK } : x)), null);
                }}
              />
              {r.ai ? (
                <textarea
                  rows={1}
                  style={{ ...inputStyle, ...growStyle, flex: 2 }}
                  placeholder="descrição para a IA"
                  value={aiFields?.[r.k] ?? ""}
                  onChange={(e) => onAiFieldsChange?.({ ...(aiFields ?? {}), [r.k]: e.target.value })}
                />
              ) : (
                <div style={{ flex: 2 }}>
                  <ExpressionEditor
                    compact autoGrow
                    minHeight={18} maxHeight={112}
                    value={r.v}
                    onChange={(v) => commitRows(rows.map((x, j) => (j === i ? { ...x, v } : x)), null)}
                  />
                </div>
              )}
              {onAiFieldsChange && (
                <label style={{ fontSize: 11, color: "var(--violet)", display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
                  <input
                    type="checkbox"
                    checked={r.ai}
                    onChange={(e) => {
                      // Toggle leva o texto junto (Fixo ↔ descrição da IA) quando o destino está vazio.
                      const on = e.target.checked;
                      const desc = aiFields?.[r.k] ?? "";
                      let v = r.v;
                      if (on && !desc && r.v && onAiFieldsChange) onAiFieldsChange({ ...(aiFields ?? {}), [r.k]: r.v });
                      if (!on && !r.v && desc) v = desc;
                      commitRows(
                        rows.map((x, j) => (j === i ? { ...x, ai: on, v } : x)),
                        on ? null : rows[i],
                      );
                    }}
                  />
                  IA
                </label>
              )}
              <button
                type="button"
                onClick={() => commitRows(rows.filter((_, j) => j !== i), rows[i])}
                aria-label="Remover"
                style={{ background: "none", color: "var(--ink-muted)", border: "none", fontSize: 16, cursor: "pointer", lineHeight: 1 }}
              >
                <XIcon size={14} />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => commitRows([...rows, { k: "", v: "", ai: false }], null)}
            style={{ alignSelf: "flex-start", background: "none", color: "var(--ink-secondary)", border: "1px solid var(--line-strong)", borderRadius: 8, padding: "4px 10px", fontSize: 12, cursor: "pointer" }}
          >
            + campo
          </button>
        </div>
      )}
    </div>
  );
}
